import i18n from 'live/services/i18n'
import api from './api'
import { isTimestampIndex, isIdIndex } from '../utils'

import { EventEmitter } from 'events'
import _ from 'lodash'

import Dispatcher from 'live/lib/dispatcher'
import Constants from './constants'

const CHANGE_EVENT = 'change'
const FILTER_DEFAULT = 'all'
const CACHE_TIMEOUT = 10000 // 10 seconds

const Factory = (namespace, actions) => {
    let _qualifier = ''
    let _loading = false
    let _timeoutId = null
    let _collectionsStats = []
    let _collectionsNames = []
    let _httpDetailedError = ''
    let _httpFailure = ''
    let _failureTitle = ''
    let _selectedFilter = FILTER_DEFAULT
    let _hideTrivial = false
    const _filters = ['all', 'withCustom', 'withoutTimestamp']
    let _statsController = null
    let _namesController = null

    const cleanErrors = function () {
        _httpDetailedError = ''
        _httpFailure = ''
        _failureTitle = ''
    }

    const cleanIfQualifierChanged = function (qualifier) {
        if (qualifier !== _qualifier) {
            _qualifier = qualifier
            _loading = false
            _collectionsStats = []
            _collectionsNames = []
            _selectedFilter = FILTER_DEFAULT
            cleanErrors()
            clearLocalTimeout()
        }
    }

    const setLoading = function (value) {
        _loading = value
        Store.emitChange()
    }

    const getAllCollectionsMappingNames = function (params) {
        const { qualifier } = params

        if (_namesController) {
            _namesController.abort()
            _namesController = new AbortController()
        }

        cleanErrors()

        api.getAllCollectionsMappingNames(
            qualifier,
            _namesController?.signal,
            data => {
                _collectionsNames = data
                Store.emitChange()
            },
            (failure, data) => {
                _failureTitle = i18n("Failure to list MongoDB collection' names")
                _httpFailure = failure
                _httpDetailedError = data
                setLoading(false)
            }
        )
    }

    const clearLocalTimeout = function () {
        if (_timeoutId) {
            clearTimeout(_timeoutId)
        }
        _timeoutId = null
    }

    const getAllCollectionsStats = function (params) {
        const { qualifier } = params

        if (_statsController) {
            _statsController.abort()
            _statsController = new AbortController()
        }

        cleanErrors()
        setLoading(true)

        api.getAllCollectionsStatsFromCache(
            qualifier,
            _statsController?.signal,
            data => {
                _collectionsStats = data.collections.map(c => {
                    c.partialResult = false

                    return c
                })
                const pending = (data.pending || []).map(c => {
                    c.partialResult = true

                    return c
                })
                _collectionsStats = [..._collectionsStats, ...pending]
                clearLocalTimeout()
                if (pending?.length !== 0) {
                    _timeoutId = setTimeout(() => {
                        actions.getAllCollectionsStats(qualifier)
                    }, CACHE_TIMEOUT)
                }

                setLoading(false)
            },
            (failure, data) => {
                _failureTitle = i18n('Failure to list MongoDB collections')
                _httpFailure = failure
                _httpDetailedError = data
                clearLocalTimeout()
                setLoading(false)
            }
        )
    }

    const deleteCollection = function (params) {
        const { qualifier, name } = params

        cleanErrors()
        setLoading(true)

        api.deleteCollection(
            qualifier,
            name,
            () => {
                const namePosition = _collectionsNames.findIndex(c => c.name === name)
                if (namePosition > -1) {
                    _collectionsNames.splice(namePosition, 1)
                }
                const statPosition = _collectionsStats.findIndex(c => c.name === name)
                if (statPosition > -1) {
                    _collectionsStats.splice(statPosition, 1)
                }
                setLoading(false)
                actions.filterCollections(qualifier, FILTER_DEFAULT)
            },
            (failure, data) => {
                _failureTitle = i18n('Failure to delete MongoDB collection')
                _httpFailure = failure
                _httpDetailedError = data
                setLoading(false)
            }
        )
    }

    const createIndex = function (params) {
        const { qualifier, collections, index } = params
        const collectionsLength = collections.length
        let collectionsCounter = 0

        cleanErrors()
        setLoading(true)

        _.map(collections, collection => {
            const indexParams = {
                collection: collection,
                name: index.name,
                key: index.key,
                sparse: index.sparse,
                partialFilterExpression: index.partialFilterExpression
            }

            api.setIndexes(
                qualifier,
                indexParams,
                () => {
                    collectionsCounter++
                    if (collectionsCounter === collectionsLength) {
                        setLoading(false)
                        actions.filterCollections(qualifier, FILTER_DEFAULT)
                        actions.getAllCollectionsMappingNames(qualifier)
                        actions.getAllCollectionsStats(qualifier)
                    }
                },
                (failure, data) => {
                    _failureTitle = i18n('Failure to create MongoDB indexes')
                    _httpFailure = failure
                    _httpDetailedError = data
                    setLoading(false)
                }
            )
        })
    }

    const deleteIndex = function (params) {
        const { qualifier, index } = params

        cleanErrors()
        setLoading(true)

        api.deleteIndex(
            qualifier,
            index,
            () => {
                const collection = _collectionsStats.find(c => c.name === index.collection)
                const indexes = collection?.indexes
                if (indexes) {
                    const position = indexes.indexOf(index)
                    if (position > -1) {
                        indexes.splice(position, 1)
                    }
                }
                const trivialIndexes = indexes.filter(i => isTimestampIndex(i) || isIdIndex(i)).length
                const customIndexes = indexes.length - trivialIndexes

                collection.trivialIndexes = trivialIndexes
                collection.customIndexes = customIndexes

                setLoading(false)
                actions.filterCollections(qualifier, FILTER_DEFAULT)
            },
            (failure, data) => {
                _failureTitle = i18n('Failure to delete MongoDB indexes')
                _httpFailure = failure
                _httpDetailedError = data
                setLoading(false)
            }
        )
    }

    const filterCollections = function (params) {
        const { filter } = params

        _selectedFilter = filter

        cleanErrors()

        Store.emitChange()
    }

    const filterDefaultIndexes = function () {
        _hideTrivial = !_hideTrivial
        cleanErrors()

        Store.emitChange()
    }

    const Store = _.merge({}, EventEmitter.prototype, {
        state: function () {
            return {
                qualifier: _qualifier,
                loadingCollections: _loading,
                collectionsStats: _collectionsStats,
                collectionsNames: _collectionsNames,
                filters: _filters,
                selectedFilter: _selectedFilter,
                hideTrivial: _hideTrivial,
                httpDetailedError: _httpDetailedError,
                httpFailure: _httpFailure,
                commandFailure: _failureTitle
            }
        },
        emitChange: function () {
            this.emit(CHANGE_EVENT)
        },
        addChangeListener: function (callback) {
            _statsController = new AbortController()
            _namesController = new AbortController()
            this.on(CHANGE_EVENT, callback)
        },
        removeChangeListener: function (callback) {
            _statsController.abort()
            _namesController.abort()
            clearLocalTimeout()
            this.removeListener(CHANGE_EVENT, callback)
        },
        dispatcher: Dispatcher.register(function (payload) {
            const action = payload.action
            const ns = action.namespace

            if (ns !== namespace) {
                return true
            }

            const params = action.params
            if (params?.qualifier) {
                cleanIfQualifierChanged(params.qualifier)
            }

            switch (action.actionType) {
                case Constants.GET_ALL_COLLECTIONS_STATS:
                    getAllCollectionsStats(params)
                    break
                case Constants.GET_ALL_COLLECTIONS_MAPPING_NAMES:
                    getAllCollectionsMappingNames(params)
                    break
                case Constants.DELETE_COLLECTION:
                    deleteCollection(params)
                    break
                case Constants.CREATE_INDEX:
                    createIndex(params)
                    break
                case Constants.DELETE_INDEX:
                    deleteIndex(params)
                    break
                case Constants.FILTER_COLLECTIONS:
                    filterCollections(params)
                    break
                case Constants.FILTER_DEFAULT_INDEXES:
                    filterDefaultIndexes(params)
                    break
                default:
                    break
            }

            return true
        })
    })

    return Store
}

export default Factory
